import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MiddlewareService } from '../../../core/services/middleware.service';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ValidationMessageComponent } from '../validation-message/validation-message.component';
import { EmailVerificationResponse } from '../../../core/models/email-verification-response.model';
import { ApiResponse } from '../../../core/enums/api-response.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { apiURL } from '../../../../assets/data/api-url';
import { map, Subscription, takeWhile, timer } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MinuteSecondTimeFormatPipe } from '../../pipes/minute-second-time-format.pipe';
import {
  NgxOtpInputComponent,
  NgxOtpInputComponentOptions,
} from 'ngx-otp-input';
import { EncryptionService } from '../../../core/services/encryption.service';
import { CaptchaComponent } from '../captcha/captcha.component';
import { preferredLanguage } from '../../../core/enums/preferred-language.enum';
import { globalDatas } from '../../../../assets/data/globals';
import { NoArabicInputDirective } from '../../directives/no-arabic-input.directive';
import { Router } from '@angular/router';
import { ModalService } from '../../../core/services/modal.service';
import { ModalType } from '../../../core/enums/modal-type.enum';
import { VisaTypes } from '../../../core/enums/visa-types.enum';
import { Component_Name } from '../../../core/enums/component-name.enum';

@Component({
  selector: 'app-email-verification',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ValidationMessageComponent,
    TranslateModule,
    CommonModule,
    MinuteSecondTimeFormatPipe,
    NgxOtpInputComponent,
    CaptchaComponent,
    NoArabicInputDirective
  ],
  templateUrl: './email-verification.component.html',
  styleUrl: './email-verification.component.scss',
})
export class EmailVerificationComponent {
  @Output() verifiedOTP = new EventEmitter<EmailVerificationResponse>();
  @Output() verificationError = new EventEmitter<any>();
  @Input() parentComponent!: string;
  @Input() accountType!: string;
  @Input() email?: string;
  @Input() additionalData?: any;
  @ViewChild('otpInput') otpInput!: NgxOtpInputComponent;
  @HostListener('document:keydown.enter', ['$event'])
  handleEnterKeyPress(event: any) {
    if (this.resendCodeTime == -1) {
      this.emailVerificationForm.get('email')?.markAsTouched();
    } else if (this.resendCodeTime != -1) {
      this.emailVerificationForm.get('otp')?.markAsTouched();
    }
    this.submitForm();
  }
  emailVerificationForm!: FormGroup;
  resendCodeTime = -1;
  otpOptions: NgxOtpInputComponentOptions = {
    otpLength: 6,
    autoFocus: true,
    autoBlur: true,
    hideInputValues: false,
    showBlinkingCursor: true,
    regexp: /^[0-9]+$/,
    ariaLabels: ['a', 'b', 'c', 'd', 'e', 'f'],
    inputMode: 'numeric',
  };
  showCaptcha = false;
  private formSubscription: Subscription | null = null;
  private countdownSubscription: Subscription | undefined;

  constructor(
    private _fb: FormBuilder,
    private _middlewareService: MiddlewareService,
    private encryptionService: EncryptionService,
    private translate: TranslateService,
    private router: Router,
    private modalService: ModalService
  ) {
    this.loadEmailVerificationForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['email']?.currentValue &&
      (this.parentComponent === 'signIn' || this.parentComponent === Component_Name.UPDATE_FAMILY_SPONSOR)) {
      this.emailVerificationForm.get('email')?.setValue(this.email);
      this.emailVerificationForm.get('email')?.disable();
      this.sendCodeToMail();
    }
  }

  ngOnInit(): void {
    const emailControl = this.emailVerificationForm.get('email');
    if (emailControl) {
      this.formSubscription = emailControl.valueChanges.subscribe((value) => {
        if (!emailControl?.disabled) {
          this.showCaptcha = false;
        }
      });
    }
  }

  loadEmailVerificationForm() {
    this.emailVerificationForm = this._fb.group({
      email: ['', [Validators.required, Validators.email, Validators.pattern(globalDatas.EMAIL_REG_EXP)]],
      otp: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  resetOtp() {
    this.otpInput?.reset();
    this.emailVerificationForm.get('otp')?.setValue('');
  }

  onOtpChange(otp: string[]) {
    this.emailVerificationForm.get('otp')?.markAsTouched();
    const hasValue = otp.some((value) => value !== '');
    if (hasValue) {
      this.emailVerificationForm.get('otp')?.setValue(otp.join(''));
    } else {
      this.emailVerificationForm.get('otp')?.setValue('');
    }
  }

  onOtpComplete(otp: string) {
    this.emailVerificationForm.get('otp')?.setValue(otp);
    this.verifyOTP()
  }

  async verifyOTP() {
    if (this.emailVerificationForm.valid) {
      const encryptedPayload = await this.encryptionService.encryptData({
        email: this.emailVerificationForm.get('email')?.value,
        otp: this.emailVerificationForm.value.otp,
        ...(this.accountType === VisaTypes.FAMILY && { mobileIdPath: 'civilIdSignUp' }),
        ...(this.additionalData?.verificationId && { verificationId: this.additionalData?.verificationId }),
      });
      let apiUrl;
      switch (this.parentComponent) {
        case 'signUp':
          apiUrl = apiURL.verifyEmailSignUp;
          break;
        case Component_Name.UPDATE_FAMILY_SPONSOR:
          apiUrl = apiURL.verifyOTPForEmailUpdate;
          break;
        default:
          apiUrl = apiURL.verifyEmail;
          break;
      }
      this._middlewareService
        .callPostMiddleware(apiUrl, { payload: encryptedPayload })
        .subscribe({
          next: (response: any) => {
            this.validateVerification(response?.code, ApiResponse.OK, response);
          },
          error: (error: any) => {
            this.verificationError.emit(error);
          },
        });
    }
  }

  validateVerification(condition1: any, condition2: any, response) {
    if (condition1 === condition2) {
      this.countdownSubscription?.unsubscribe();
      this.resendCodeTime = -1;
      this.showCaptcha = false;
      this.verifiedOTP.emit({
        email: this.emailVerificationForm.get('email')?.value,
        status: true,
        response: response
      });
    }
  }

  async sendCodeToMail() {
    this.resetOtp();
    this.resendCodeTime = -1;
    let payload;
    let apiUrl;
    switch (this.parentComponent) {
      case 'signUp':
        apiUrl = apiURL.signUpEmailVerification;
        payload = {
          email: this.emailVerificationForm.get('email')?.value,
          preferredLanguage:
            this.translate.currentLang === 'en'
              ? preferredLanguage.ENGLISH
              : preferredLanguage.ARABIC,
        };
        break;
      case Component_Name.UPDATE_FAMILY_SPONSOR:
        apiUrl = apiURL.sentotpForEditFamily;
        payload = {
          email: this.emailVerificationForm.get('email')?.value,
          preferredLanguage:
            this.translate.currentLang === 'en'
              ? preferredLanguage.ENGLISH
              : preferredLanguage.ARABIC,
        };
        break;
      default:
        apiUrl = apiURL.signInEmailVerification;
        payload = {
          email: this.emailVerificationForm.get('email')?.value,
          isSignIn: this.parentComponent === 'signIn' ? true : false,
        };
    }
    const encryptedPayload = await this.encryptionService.encryptData(payload);
    this._middlewareService
      .callPostMiddleware(apiUrl, { payload: encryptedPayload })
      .subscribe({
        next: (response: any) => {
          if (this.parentComponent === "forgotPassword") {
            this.modalService.commonAlertModal("EMAIL_VERIFICATION.VERIFICATION_LINK_SENT", ModalType.SUCCESS)
            this.emailVerificationForm.get('email')?.disable();
            const expiryTime = new Date().getTime() + 10000; // Convert to timestamp
            this.startCountdown(expiryTime);
          }
          else {
            if (response?.code === ApiResponse.OK) {
              this.emailVerificationForm.get('email')?.disable();
              const expiryTime = new Date(response?.result?.expiryTime).getTime(); // Convert to timestamp
              this.startCountdown(expiryTime);
            } else {
              this.emailVerificationForm.get('email')?.enable();
            }
          }
        },
        error: (error: any) => {
          this.verificationError.emit(error);
        },
      });
  }
  //-1 is the initial value
  //0 to resend button

  startCountdown(expiryTimestamp: number) {
    this.countdownSubscription?.unsubscribe();
    this.countdownSubscription = timer(0, 1000)
      .pipe(
        map(() => this.calculateRemainingTime(expiryTimestamp)),
        takeWhile((time) => time >= 0)
      )
      .subscribe({
        next: (time) => {
          this.resendCodeTime = time;
        },
        error: (err) => {
          this.resendCodeTime = 0;
        },
        complete: () => {
          this.resendCodeTime = 0;
          if (this.parentComponent === "forgotPassword") {
            this.router.navigate(['/'])
          }
        },
      });
  }

  calculateRemainingTime(expiryTimestamp: number): number {
    const currentTime = Date.now();
    return expiryTimestamp - currentTime;
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
    this.countdownSubscription?.unsubscribe();
  }

  submitForm() {
    this.resendCodeTime === -1 && this.emailVerificationForm.get('email')?.valid
      ? this.onClickEmailButton()
      : this.resendCodeTime !== -1 &&
        this.emailVerificationForm.get('otp')?.valid
        ? this.verifyOTP()
        : '';
  }

  handleRecaptchaResponse(response: boolean) {
    if (response) {
      this.sendCodeToMail();
    }
  }

  onClickEmailButton() {
    if (this.emailVerificationForm.get('email')?.valid) {
      this.showCaptcha = true;
      // this.sendCodeToMail();

    }
  }

  emailInputChanges(event: KeyboardEvent) {
    if (this.emailVerificationForm.get('email')?.disabled) {
      event.preventDefault();
    }
  }

}
